import Dexie, { type Table } from "dexie";
import type { CreatePhotoDocUpload } from "~generated";
import { config } from "~initializer/config.ts";
import {
  type MigrationStep,
  migrateRecord,
  migrateRecords,
  needsMigration,
  type VersionedRecord,
} from "./recordMigration.ts";

export const PHOTO_DOC_QUEUE_UPDATED_EVENT = "photo-doc-queue-updated";

export type PhotoDocUploadStatus =
  | "pending"
  | "uploading"
  | "failed"
  | "completed";

export interface PendingPhotoDocUpload extends VersionedRecord {
  id: string;
  tenantId: string;
  workPackageId: string | null;
  workPackageName: string | null;
  constructionSiteNumber: string | null;
  constructionSiteManagerId: string | null;
  constructionSiteManagerName: string | null;
  taskCustomerName: string | null;
  taskStreet: string | null;
  taskHouseNumber: string | null;
  taskZip: string | null;
  taskCity: string | null;
  taskPhone: string | null;
  taskDescription: string | null;
  comment: string | null;
  fileCount: number;
  status: PhotoDocUploadStatus;
  retries: number;
  createdAt: number;
  /** Earliest timestamp (ms) at which this upload may be retried. */
  nextRetryAt: number;
  /** Timestamp (ms) when this upload last transitioned to "uploading". */
  uploadingStartedAt?: number;
  /** Timestamp (ms) at which a manual retry restarted the retry window. */
  retryWindowStartedAt?: number;
}

/** Per-photo metadata and preview, kept apart from the bytes so listing is cheap. */
export interface PhotoDocUploadFile {
  uploadId: string;
  index: number;
  name: string;
  type: string;
  latitude: number | null;
  longitude: number | null;
  gpsSource: CreatePhotoDocUpload.gps_source | null;
  thumbnail: ArrayBuffer | undefined;
}

interface PhotoDocUploadBlob {
  uploadId: string;
  index: number;
  blob: ArrayBuffer;
}

/** A photo as handed over by the upload form. */
export interface PhotoDocUploadPhoto {
  file: File;
  thumbnail: File | undefined;
  latitude: number | null;
  longitude: number | null;
  gpsSource: CreatePhotoDocUpload.gps_source | null;
}

/** Photo inlined into the upload record itself, as written before schema v3. */
interface LegacyPhotoDocUploadFile {
  blob: ArrayBuffer;
  thumbnail: ArrayBuffer | undefined;
  name: string;
  type: string;
  latitude: number | null;
  longitude: number | null;
  gpsSource: CreatePhotoDocUpload.gps_source | null;
}

type LegacyPendingPhotoDocUpload = PendingPhotoDocUpload & {
  files: LegacyPhotoDocUploadFile[];
};

function hasInlinePhotos(
  upload: PendingPhotoDocUpload,
): upload is LegacyPendingPhotoDocUpload {
  return Array.isArray((upload as Partial<LegacyPendingPhotoDocUpload>).files);
}

class PhotoDocQueueDB extends Dexie {
  pendingUploads!: Table<PendingPhotoDocUpload, string>;
  uploadFiles!: Table<PhotoDocUploadFile, [string, number]>;
  uploadBlobs!: Table<PhotoDocUploadBlob, [string, number]>;

  constructor() {
    super("grewe-gruppe-photo-doc-queue");
    this.version(1).stores({
      pendingUploads: "id, status, workPackageId",
    });
    this.version(2).stores({
      pendingUploads: "id, status, workPackageId, createdAt",
    });
    // No upgrade callback on purpose: it would iterate every record in one
    // blocking transaction, the very operation that fails on these queues.
    // recordMigration.ts moves the photos over lazily instead.
    this.version(3).stores({
      pendingUploads: "id, status, workPackageId, createdAt",
      uploadFiles: "[uploadId+index], uploadId",
      uploadBlobs: "[uploadId+index], uploadId",
    });
  }
}

export const photoDocQueueDB = new PhotoDocQueueDB();

/** Enqueues that have written photos but not their upload record yet. */
const enqueueing = new Set<string>();

export async function addPhotoDocUpload(
  upload: Omit<
    PendingPhotoDocUpload,
    "status" | "retries" | "nextRetryAt" | "fileCount"
  >,
  photos: PhotoDocUploadPhoto[],
): Promise<void> {
  enqueueing.add(upload.id);
  try {
    await writeUpload(upload, photos);
  } catch (err) {
    // The user retries with a fresh id, so photos of the failed attempt would
    // stay behind and eat into the storage quota.
    await deleteUploadPhotos(upload.id).catch(() => {});
    throw err;
  } finally {
    enqueueing.delete(upload.id);
  }
}

async function writeUpload(
  upload: Omit<
    PendingPhotoDocUpload,
    "status" | "retries" | "nextRetryAt" | "fileCount"
  >,
  photos: PhotoDocUploadPhoto[],
): Promise<void> {
  // Photos first, upload record last. Enqueueing is a sequence of writes, and
  // a queue run triggered in between would find an upload without photos and
  // fail it permanently.
  for (const [index, photo] of photos.entries()) {
    await photoDocQueueDB.uploadFiles.put({
      uploadId: upload.id,
      index,
      name: photo.file.name,
      type: photo.file.type,
      latitude: photo.latitude,
      longitude: photo.longitude,
      gpsSource: photo.gpsSource,
      thumbnail: await photo.thumbnail?.arrayBuffer(),
    });
    await photoDocQueueDB.uploadBlobs.put({
      uploadId: upload.id,
      index,
      blob: await photo.file.arrayBuffer(),
    });
  }
  await photoDocQueueDB.pendingUploads.add({
    ...upload,
    schemaVersion: PHOTO_DOC_SCHEMA_VERSION,
    fileCount: photos.length,
    status: "pending",
    retries: 0,
    nextRetryAt: 0,
  });
}

export async function updatePhotoDocUpload(
  id: string,
  changes: Partial<
    Pick<
      PendingPhotoDocUpload,
      | "status"
      | "retries"
      | "nextRetryAt"
      | "uploadingStartedAt"
      | "retryWindowStartedAt"
    >
  >,
): Promise<void> {
  await photoDocQueueDB.pendingUploads.update(id, changes);
}

/**
 * Also restarts the retry window: otherwise a manual retry of an expired
 * upload is failed again before a single attempt (GREW-2872).
 */
export async function retryPhotoDocUpload(id: string): Promise<void> {
  await updatePhotoDocUpload(id, {
    status: "pending",
    nextRetryAt: 0,
    retries: 0,
    retryWindowStartedAt: Date.now(),
  });
}

export async function deletePhotoDocUpload(id: string): Promise<void> {
  // Upload record first: an interrupted cascade leaves recoverable orphans,
  // not an upload whose photos are gone.
  await photoDocQueueDB.pendingUploads.delete(id);
  await deleteUploadPhotos(id);
}

async function deleteUploadPhotos(uploadId: string): Promise<void> {
  await photoDocQueueDB.uploadBlobs.where("uploadId").equals(uploadId).delete();
  await photoDocQueueDB.uploadFiles.where("uploadId").equals(uploadId).delete();
}

export async function deleteCompletedUploads(): Promise<void> {
  const ids = await photoDocQueueDB.pendingUploads
    .where("status")
    .equals("completed")
    .primaryKeys();
  for (const id of ids) {
    await deletePhotoDocUpload(id);
  }
}

export async function getUploadFiles(
  uploadId: string,
): Promise<PhotoDocUploadFile[]> {
  return photoDocQueueDB.uploadFiles
    .where("uploadId")
    .equals(uploadId)
    .sortBy("index");
}

export async function getUploadBlob(
  uploadId: string,
  index: number,
): Promise<ArrayBuffer | undefined> {
  const record = await photoDocQueueDB.uploadBlobs.get([uploadId, index]);
  return record?.blob;
}

/** Bumped whenever a migration step is added below. */
export const PHOTO_DOC_SCHEMA_VERSION = 3;

/** v2 kept every photo inside the upload record; v3 puts each one in its own. */
const splitInlinePhotos: MigrationStep<PendingPhotoDocUpload> = {
  from: 2,
  async upgrade(record) {
    // Records an earlier v3 build already split carry no version stamp yet;
    // the runner adds it.
    if (!hasInlinePhotos(record)) return record;

    const { files, ...metadata } = record;
    for (const [index, file] of files.entries()) {
      const { blob, ...fileMetadata } = file;
      await photoDocQueueDB.uploadFiles.put({
        uploadId: record.id,
        index,
        ...fileMetadata,
      });
      await photoDocQueueDB.uploadBlobs.put({
        uploadId: record.id,
        index,
        blob,
      });
    }

    // Each write above committed on its own, so read back before returning the
    // record without its only other copy of the photos.
    await assertPhotosPersisted(record.id, files.length);

    return { ...metadata, fileCount: files.length };
  },
};

const PHOTO_DOC_MIGRATIONS = [splitInlinePhotos];

export function isOutdatedUpload(upload: PendingPhotoDocUpload): boolean {
  return needsMigration(upload, PHOTO_DOC_MIGRATIONS);
}

export async function migratePhotoDocUpload(
  id: string,
): Promise<PendingPhotoDocUpload | undefined> {
  return migrateRecord(
    photoDocQueueDB.pendingUploads,
    id,
    PHOTO_DOC_MIGRATIONS,
  );
}

export async function migratePhotoDocUploads(
  onError?: (err: unknown, uploadId: string) => void,
): Promise<void> {
  return migrateRecords(
    photoDocQueueDB.pendingUploads,
    PHOTO_DOC_MIGRATIONS,
    onError,
  );
}

let migrationRun: Promise<void> | null = null;

export function whenQueueMigrated(
  onError?: (err: unknown, uploadId?: string) => void,
): Promise<void> {
  migrationRun ??= migratePhotoDocUploads(onError).catch((err: unknown) => {
    // A sweep that failed as a whole may well succeed on a later trigger
    migrationRun = null;
    onError?.(err);
  });
  return migrationRun;
}

async function assertPhotosPersisted(
  uploadId: string,
  expected: number,
): Promise<void> {
  const [files, blobs] = await Promise.all([
    photoDocQueueDB.uploadFiles.where("uploadId").equals(uploadId).count(),
    photoDocQueueDB.uploadBlobs.where("uploadId").equals(uploadId).count(),
  ]);
  if (files !== expected || blobs !== expected) {
    throw new Error(
      `Migration of upload ${uploadId} persisted ${files} file and ${blobs} blob records instead of ${expected}`,
    );
  }
}

/** Drops photo rows whose upload record is gone: an interrupted enqueue or delete. */
export async function pruneOrphanedUploadPhotos(): Promise<void> {
  const existing = new Set(
    await photoDocQueueDB.pendingUploads.toCollection().primaryKeys(),
  );
  // primaryKeys() reads without a cursor, which iOS Safari fails to open (GGA-APP-2MC)
  const referenced = new Set(
    [
      ...(await photoDocQueueDB.uploadFiles.toCollection().primaryKeys()),
      ...(await photoDocQueueDB.uploadBlobs.toCollection().primaryKeys()),
    ].map(([uploadId]) => uploadId),
  );
  for (const uploadId of referenced) {
    // An enqueue in flight has photos but no upload record yet; deleting those
    // would leave it without its photos. Only covers this tab.
    if (existing.has(uploadId) || enqueueing.has(uploadId)) continue;
    await deleteUploadPhotos(uploadId);
  }
}

const STUCK_UPLOAD_THRESHOLD_MS = 30 * 60_000; // 30 minutes (covers ~20 photos on slow 3G)

export async function resetActiveUploads(): Promise<void> {
  await photoDocQueueDB.pendingUploads
    .where("status")
    .equals("uploading")
    .modify({ status: "pending" });
}

export async function resetStuckUploads(): Promise<void> {
  const stuckBefore = Date.now() - STUCK_UPLOAD_THRESHOLD_MS;
  await photoDocQueueDB.pendingUploads
    .where("status")
    .equals("uploading")
    .and(
      (u) =>
        u.uploadingStartedAt === undefined ||
        u.uploadingStartedAt < stuckBefore,
    )
    .modify({ status: "pending" });
}

export async function getPendingPhotoDocUploads(
  { ignoreNextRetryAt }: { ignoreNextRetryAt: boolean } = {
    ignoreNextRetryAt: false,
  },
  onError?: (err: unknown, uploadId: string) => void,
): Promise<PendingPhotoDocUpload[]> {
  const now = Date.now();
  const ids = await photoDocQueueDB.pendingUploads
    .where("status")
    .anyOf("pending", "failed")
    .primaryKeys();

  const due: PendingPhotoDocUpload[] = [];
  for (const id of ids) {
    try {
      const upload = await photoDocQueueDB.pendingUploads.get(id);
      if (!upload) continue;
      if (ignoreNextRetryAt || upload.nextRetryAt <= now) due.push(upload);
    } catch (err) {
      onError?.(err, id);
    }
  }
  return due;
}

export function uploadExpiresAt(upload: PendingPhotoDocUpload): number {
  const windowStart = upload.retryWindowStartedAt ?? upload.createdAt;
  return windowStart + config().photoDocUploadMaxRetryHours * 60 * 60 * 1000;
}

export function isUploadExpired(upload: PendingPhotoDocUpload): boolean {
  return Date.now() >= uploadExpiresAt(upload);
}
